<template>
  <div class="d-flex flex-column" style="height: 100vh;">
     <!-- Header and Actions -->
     <div class="px-4 px-md-8 pt-8 pb-2 flex-shrink-0">
        <div class="d-flex justify-space-between align-center mb-2">
            <div class="text-h5 font-weight-bold">
                {{ editTenant.id ? $t("edit_customer") : $t("create_customer") }}
            </div>
            <v-btn
                text
                fab
                small
                @click="close()"
                :ripple="false"
            >
                <img width='24' height='24' src="../../assets/img/svg/icons/close.svg" alt="close">
            </v-btn>
        </div>
     </div>

     <!-- Scrollable Form Content -->
     <div class="flex-grow-1 overflow-y-auto px-4 px-md-8">
        <v-form ref="form" class="mt-2">
            <!-- Customer Info -->
            <div class="text-h6 mb-3 font-weight-medium grey--text text--darken-2">
                {{ $t("customer_info") }}
            </div>

            <v-row dense>
                <v-col cols="12" md="8">
                    <v-text-field
                        v-model="editTenant.name"
                        :label="$t('name')"
                        outlined
                        dense
                        :rules="rules"
                        hide-details="auto"
                        class="rounded-0 mb-2"
                    ></v-text-field>
                </v-col>

                <v-col cols="12" md="4">
                    <v-select
                        v-model="editTenant.clientType"
                        :items="clientTypeOptions"
                        :label="$t('customer_type')"
                        outlined
                        dense
                        hide-details="auto"
                        class="rounded-0 mb-2"
                        :menu-props="{ contentClass: 'white-dropdown', offsetY: true }"
                    ></v-select>
                </v-col>
            </v-row>

            <template v-if="editTenant.clientType === 'Company'">
                <v-row dense>
                    <v-col cols="12" md="6">
                         <v-text-field
                            v-model="editTenant.vat"
                            :label="$t('vat_id')"
                            outlined
                            dense
                            hide-details="auto"
                            class="rounded-0 mb-2"
                        ></v-text-field>
                    </v-col>
                     <v-col cols="12" md="6">
                        <v-select
                            v-model="editTenant.formOfBusiness"
                            :items="['GmbH', 'AG', 'UG', 'KG', 'OHG', 'Einzelunternehmen']"
                            :label="$t('business_form')"
                            outlined
                            dense
                            hide-details="auto"
                            class="rounded-0 mb-2"
                            :menu-props="{ contentClass: 'white-dropdown', offsetY: true }"
                        ></v-select>
                    </v-col>
                </v-row>
            </template>


            <v-row dense class="mt-4">
                 <v-col cols="12">
                    <v-text-field
                        v-model="editTenant.streetAddress"
                        :label="$t('street_and_house_number')"
                        outlined
                        dense
                        hide-details="auto"
                        class="rounded-0 mb-2"
                    ></v-text-field>
                 </v-col>
                 <v-col cols="12" md="4">
                    <v-text-field
                        v-model="editTenant.zipCode"
                        :label="$t('postal_code')"
                        outlined
                        dense
                        hide-details="auto"
                        class="rounded-0 mb-2"
                    ></v-text-field>
                 </v-col>
                 <v-col cols="12" md="8">
                     <v-text-field
                        v-model="editTenant.city"
                        :label="$t('city')"
                        outlined
                        dense
                        hide-details="auto"
                        class="rounded-0 mb-2"
                    ></v-text-field>
                 </v-col>
                  <v-col cols="12" md="6">
                     <v-autocomplete
                        v-model="editTenant.country"
                        :items="countries"
                        :label="$t('country_label')"
                        outlined
                        dense
                        hide-details="auto"
                        class="rounded-0 mb-2"
                        :menu-props="{ contentClass: 'white-dropdown', offsetY: true }"
                    ></v-autocomplete>
                 </v-col>
                 <v-col cols="12" md="6">
                    <v-text-field
                        v-model="editTenant.phoneNumber"
                        :label="$t('phone_number_optional')"
                        outlined
                        dense
                        hide-details="auto"
                        class="rounded-0 mb-2"
                    ></v-text-field>
                 </v-col>
            </v-row>


            <v-divider class="my-6"></v-divider>

            <!-- Admin Info -->
            <div class="mb-6">
                <div class="text-h6 font-weight-medium grey--text text--darken-2">
                    {{ $t("customer_admin_info") }}
                </div>
                <div class="caption grey--text">
                    {{ $t("customer_admin_description") }}
                </div>
            </div>

            <!-- Create Mode: New Admin -->
            <v-row dense v-if="!editTenant.id">
                <v-col cols="12" md="6">
                    <v-text-field
                        :readonly="!!editTenant.id"
                        v-model="editTenant.tenantAdmin.firstName"
                        :label="$t('firstname')"
                        outlined
                        dense
                        :rules="rules"
                        hide-details="auto"
                        class="rounded-0 mb-4"
                    ></v-text-field>
                </v-col>
                 <v-col cols="12" md="6">
                     <v-text-field
                        :readonly="!!editTenant.id"
                        v-model="editTenant.tenantAdmin.lastName"
                        :label="$t('lastname')"
                        outlined
                        dense
                        :rules="rules"
                        hide-details="auto"
                        class="rounded-0 mb-4"
                    ></v-text-field>
                 </v-col>
                 <v-col cols="12">
                     <v-text-field
                        :readonly="!!editTenant.id"
                        v-model="editTenant.tenantAdmin.email"
                        :label="$t('email')"
                        outlined
                        dense
                        :rules="emailRules"
                        hide-details="auto"
                        class="rounded-0 mb-4"
                    ></v-text-field>
                 </v-col>
                 <v-col cols="12" class="mb-4">
                     <v-checkbox
                        :readonly="!!editTenant.id"
                        v-model="editTenant.sendInvitationMail"
                        :label="$t('send_invitation_mail')"
                        hide-details
                        class="mt-0 pt-0"
                    ></v-checkbox>
                 </v-col>
                 <v-col cols="12" v-if="editTenant.sendInvitationMail">
                     <v-select
                        :items="languageOptions"
                        v-model="selectedLanguage"
                        :label="$t('email_language')"
                        outlined
                        dense
                        hide-details="auto"
                        class="rounded-0 mb-4"
                        :menu-props="{ contentClass: 'white-dropdown', offsetY: true }"
                    ></v-select>
                 </v-col>
            </v-row>

            <!-- Edit Mode: Select Admin -->
            <v-row dense v-if="editTenant.id">
                <v-col cols="12">
                     <v-select
                        v-model="selectedTenantAdmin"
                        :items="tenantUsers"
                        item-text="displayName"
                        item-value="id"
                        :label="$t('tenant') + ' Admin'"
                        outlined
                        dense
                        hide-details="auto"
                        @change="assignTenantAdmin"
                        class="rounded-0 mb-2"
                    ></v-select>
                </v-col>
                <v-col cols="12" v-if="currentTenantAdmin">
                    <div class="caption grey--text text--darken-2">
                        {{ $t('current') }}: <strong>{{ currentTenantAdmin.displayName }}</strong> ({{ currentTenantAdmin.mail }})
                    </div>
                </v-col>
            </v-row>

        </v-form>
     </div>
    
     <!-- Footer Actions -->
     <div class="px-4 px-md-8 pt-4 pb-8 flex-shrink-0">
        <v-btn
          v-if="!editTenant.id"
          class="white--text"
          color="#161616"
          tile
          large
          block
          depressed
          @click="createCustomer()"
        >
          {{ $t("create_new_customer") }}
        </v-btn>
        <v-btn
          v-if="editTenant.id"
          class="white--text"
          color="#161616"
          tile
          large
          block
          depressed
          @click="editCustomer()"
        >
          {{ $t("save_changes") }}
        </v-btn>
     </div>
  </div>
</template>

<script>
export default {

    props: {
    navObject: Object,
    editTenant: Object,
    metadata: Object,
    close: Function,
    reload: Function,
    removeDevice: Function,
    },

    data() {
        return {
        dots: 6,
        routes: {
            users: "api/users",
        },
        emailRules: [
        (v) => !!v || this.$t("email_required"),
        (v) =>
          /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(v) ||
          this.$t("email_valid"),
        ],
        rules: [(value) => !!value || this.$t("required")],
        textfieldtype: false,
        me: null,
        isEditingOwnUser: false, // Track if editing own profile to prevent layout shift
        tenantUsers: [],
        selectedTenantAdmin: null,
        currentTenantAdmin: null,
        selectedLanguage: null, // Language for invitation email
        languageOptions: [
            { text: 'Deutsch', value: 'de' },
            { text: 'English', value: 'en' }
        ]
        }
    },

    computed: {
        clientTypeOptions() {
            return [
                { text: this.$t('organisation'), value: 'Organisation' },
                { text: this.$t('company'), value: 'Company' },
                { text: this.$t('private_person'), value: 'PrivatePerson' }
            ];
        },
        countries() {
            return [
                this.$t('country.germany'), this.$t('country.austria'), this.$t('country.switzerland'), this.$t('country.france'),
                this.$t('country.united_kingdom'), this.$t('country.united_states'), this.$t('country.afghanistan'), this.$t('country.albania'),
                this.$t('country.algeria'), this.$t('country.andorra'), this.$t('country.angola'), this.$t('country.antigua_and_barbuda'),
                this.$t('country.argentina'), this.$t('country.armenia'), this.$t('country.australia'), this.$t('country.azerbaijan'),
                this.$t('country.bahamas'), this.$t('country.bahrain'), this.$t('country.bangladesh'), this.$t('country.barbados'),
                this.$t('country.belarus'), this.$t('country.belgium'), this.$t('country.belize'), this.$t('country.benin'),
                this.$t('country.bhutan'), this.$t('country.bolivia'), this.$t('country.bosnia_and_herzegovina'), this.$t('country.botswana'),
                this.$t('country.brazil'), this.$t('country.brunei'), this.$t('country.bulgaria'), this.$t('country.burkina_faso'),
                this.$t('country.burundi'), this.$t('country.cabo_verde'), this.$t('country.cambodia'), this.$t('country.cameroon'),
                this.$t('country.canada'), this.$t('country.central_african_republic'), this.$t('country.chad'), this.$t('country.chile'),
                this.$t('country.china'), this.$t('country.colombia'), this.$t('country.comoros'), this.$t('country.congo_congo_brazzaville'),
                this.$t('country.costa_rica'), this.$t('country.croatia'), this.$t('country.cuba'), this.$t('country.cyprus'),
                this.$t('country.czechia_czech_republic'), this.$t('country.democratic_republic_of_the_congo'), this.$t('country.denmark'),
                this.$t('country.djibouti'), this.$t('country.dominica'), this.$t('country.dominican_republic'), this.$t('country.ecuador'),
                this.$t('country.egypt'), this.$t('country.el_salvador'), this.$t('country.equatorial_guinea'), this.$t('country.eritrea'),
                this.$t('country.estonia'), this.$t('country.eswatini_fmr_swaziland'), this.$t('country.ethiopia'), this.$t('country.fiji'),
                this.$t('country.finland'), this.$t('country.gabon'), this.$t('country.gambia'), this.$t('country.georgia'),
                this.$t('country.ghana'), this.$t('country.greece'), this.$t('country.grenada'), this.$t('country.guatemala'),
                this.$t('country.guinea'), this.$t('country.guinea_bissau'), this.$t('country.guyana'), this.$t('country.haiti'),
                this.$t('country.holy_see'), this.$t('country.honduras'), this.$t('country.hungary'), this.$t('country.iceland'),
                this.$t('country.india'), this.$t('country.indonesia'), this.$t('country.iran'), this.$t('country.iraq'),
                this.$t('country.ireland'), this.$t('country.israel'), this.$t('country.italy'), this.$t('country.ivory_coast'),
                this.$t('country.jamaica'), this.$t('country.japan'), this.$t('country.jordan'), this.$t('country.kazakhstan'),
                this.$t('country.kenya'), this.$t('country.kiribati'), this.$t('country.kuwait'), this.$t('country.kyrgyzstan'),
                this.$t('country.laos'), this.$t('country.latvia'), this.$t('country.lebanon'), this.$t('country.lesotho'),
                this.$t('country.liberia'), this.$t('country.libya'), this.$t('country.liechtenstein'), this.$t('country.lithuania'),
                this.$t('country.luxembourg'), this.$t('country.madagascar'), this.$t('country.malawi'), this.$t('country.malaysia'),
                this.$t('country.maldives'), this.$t('country.mali'), this.$t('country.malta'), this.$t('country.marshall_islands'),
                this.$t('country.mauritania'), this.$t('country.mauritius'), this.$t('country.mexico'), this.$t('country.micronesia'),
                this.$t('country.moldova'), this.$t('country.monaco'), this.$t('country.mongolia'), this.$t('country.montenegro'),
                this.$t('country.morocco'), this.$t('country.mozambique'), this.$t('country.myanmar_formerly_burma'), this.$t('country.namibia'),
                this.$t('country.nauru'), this.$t('country.nepal'), this.$t('country.netherlands'), this.$t('country.new_zealand'),
                this.$t('country.nicaragua'), this.$t('country.niger'), this.$t('country.nigeria'), this.$t('country.north_korea'),
                this.$t('country.north_macedonia'), this.$t('country.norway'), this.$t('country.oman'), this.$t('country.pakistan'),
                this.$t('country.palau'), this.$t('country.palestine_state'), this.$t('country.panama'), this.$t('country.papua_new_guinea'),
                this.$t('country.paraguay'), this.$t('country.peru'), this.$t('country.philippines'), this.$t('country.poland'),
                this.$t('country.portugal'), this.$t('country.qatar'), this.$t('country.romania'), this.$t('country.russia'),
                this.$t('country.rwanda'), this.$t('country.saint_kitts_and_nevis'), this.$t('country.saint_lucia'),
                this.$t('country.saint_vincent_and_the_grenadines'), this.$t('country.samoa'), this.$t('country.san_marino'),
                this.$t('country.sao_tome_and_principe'), this.$t('country.saudi_arabia'), this.$t('country.senegal'),
                this.$t('country.serbia'), this.$t('country.seychelles'), this.$t('country.sierra_leone'), this.$t('country.singapore'),
                this.$t('country.slovakia'), this.$t('country.slovenia'), this.$t('country.solomon_islands'), this.$t('country.somalia'),
                this.$t('country.south_africa'), this.$t('country.south_korea'), this.$t('country.south_sudan'), this.$t('country.spain'),
                this.$t('country.sri_lanka'), this.$t('country.sudan'), this.$t('country.suriname'), this.$t('country.sweden'),
                this.$t('country.syria'), this.$t('country.tajikistan'), this.$t('country.tanzania'), this.$t('country.thailand'),
                this.$t('country.timor_leste'), this.$t('country.togo'), this.$t('country.tonga'), this.$t('country.trinidad_and_tobago'),
                this.$t('country.tunisia'), this.$t('country.turkey'), this.$t('country.turkmenistan'), this.$t('country.tuvalu'),
                this.$t('country.uganda'), this.$t('country.ukraine'), this.$t('country.united_arab_emirates'), this.$t('country.uruguay'),
                this.$t('country.uzbekistan'), this.$t('country.vanuatu'), this.$t('country.venezuela'), this.$t('country.vietnam'),
                this.$t('country.yemen'), this.$t('country.zambia'), this.$t('country.zimbabwe')
            ].sort((a, b) => a.localeCompare(b));
        }
    },

    mounted() {
        this.fetchMe();
        if (this.editTenant.id) {
            this.loadTenantUsers();
            // Load address data from editTenant if it exists
            if (this.editTenant.address) {
                this.editTenant.streetAddress = this.editTenant.address.streetAddress;
                this.editTenant.zipCode = this.editTenant.address.zipCode;
                this.editTenant.city = this.editTenant.address.city;
                this.editTenant.country = this.editTenant.address.country;
            }
        } else {
            // Set default values for new tenant
            if (!this.editTenant.clientType) {
                this.editTenant.clientType = 'Organisation';
            }
            if (!this.editTenant.country) {
                this.editTenant.country = 'Deutschland';
            }
        }
        // Initialize language selector with current user's language (or browser language as fallback)
        this.selectedLanguage = this.$getBrowserLanguage();
    },

    watch: {
        filteredRoleNames: {
            handler(newRoles) {
                // Auto-select default "User" role when creating a new user
                if (this.navObject.createmode && newRoles && newRoles.length > 0 && !this.editTenant.tenant.role) {
                    // Find the role named "User" or "Benutzer"
                    const defaultRole = newRoles.find(roleItem =>
                        roleItem.text === 'User' ||
                        roleItem.text === 'Benutzer' ||
                        roleItem.value?.name === 'User' ||
                        roleItem.value?.name === 'Benutzer'
                    );

                    if (defaultRole) {
                        this.editTenant.tenant.role = defaultRole.value;
                    } else if (newRoles.length > 0) {
                        // If no "User" role found, select the first available role
                        this.editTenant.tenant.role = newRoles[0].value;
                    }
                }
            },
            immediate: true
        },
        'editTenant.id': {
            handler(newId) {
                if (newId) {
                    this.loadTenantUsers();
                }
            },
            immediate: true
        }
    },

    methods: {
        async fetchMe() {
            var _this = this;
            await this.$axios
                .get("api/me")
                .then(function (response) {
                    _this.me = response.data;

                    // Check if editing own profile to prevent layout shift
                    _this.isEditingOwnUser = _this.navObject.editmode &&
                                             _this.editTenant.tenant &&
                                             _this.me.id === _this.editTenant.tenant.id;

                    // Auto-assign tenant for Tenant Admin when creating users
                    if (_this.isTenantAdmin() && _this.navObject.createmode && _this.me.customer != null) {
                        _this.editTenant.tenant.customer = _this.me.customer;
                    }

                    // Set default language for invitation to current user's language
                    if (_this.me.language) {
                        _this.selectedLanguage = _this.me.language;
                    }
                })
                .catch(this.onError);
        },
        isTenantAdmin() {
            if (!this.me) return false;
            // A Tenant Admin has ManageTenantUsers or ManageTenant rights but NOT GlobalAccess
            const hasManageRights = this.me.rights.includes("ManageTenantUsers") ||
                                   this.me.rights.includes("ManageTenant") ||
                                   (this.me.role && (this.me.role.rights.includes("ManageTenantUsers") ||
                                                     this.me.role.rights.includes("ManageTenant")));
            const hasGlobalAccess = this.me.rights.includes("GlobalAccess") ||
                                   (this.me.role && this.me.role.rights.includes("GlobalAccess"));
            return hasManageRights && !hasGlobalAccess;
        },
        createCustomer() {
        var _this = this;
        // Add language to the request
        const requestData = {
            ...this.editTenant,
            language: this.selectedLanguage || 'de'
        };
        this.$axios
            .post("api/customers", requestData)
            .then(function (response) {
                _this.$noty.success('<i class="fas fa-check-circle mr-3"></i>' + _this.$t("customer_created"));
                _this.close();
                _this.reload();
            })
            .catch(this.onError);
        },
        editCustomer() {
        var _this = this;
        this.$axios
            .put("api/customers", this.editTenant)
            .then(function (response) {
                _this.$noty.success('<i class="fas fa-check-circle mr-3"></i>' + _this.$t("customer_updated"));
                _this.close();
                _this.reload();
            })
            .catch(this.onError);
        },
        loadTenantUsers() {
            if (!this.editTenant.id) return;

            var _this = this;
            this.$axios
                .get(`api/customers/${this.editTenant.id}/users`, {
                    params: { page: 0, size: 1000 }
                })
                .then(function (response) {
                    _this.tenantUsers = response.data.content.map(user => ({
                        id: user.id,
                        displayName: `${user.personalData.firstname} ${user.personalData.lastname}`,
                        mail: user.mail,
                        roleName: user.role?.name,
                        rights: user.rights || [],
                        roleRights: user.role?.rights || []
                    }));

                    // Find current tenant admin - first user with ManageTenant or ManageTenantUsers right but NOT GlobalAccess
                    _this.currentTenantAdmin = _this.tenantUsers.find(user => {
                        const hasManageRights = user.rights.includes('ManageTenant') || 
                                               user.rights.includes('ManageTenantUsers') ||
                                               user.roleRights.includes('ManageTenant') ||
                                               user.roleRights.includes('ManageTenantUsers');
                        const hasGlobalAccess = user.rights.includes('GlobalAccess') ||
                                               user.roleRights.includes('GlobalAccess');
                        return hasManageRights && !hasGlobalAccess;
                    });
                    
                    if (_this.currentTenantAdmin) {
                        _this.selectedTenantAdmin = _this.currentTenantAdmin.id;
                    }
                })
                .catch(this.onError);
        },
        assignTenantAdmin() {
            if (!this.selectedTenantAdmin) return;

            var _this = this;
            this.$axios
                .post(`api/customers/${this.editTenant.id}/tenant-admin/${this.selectedTenantAdmin}`)
                .then(function (response) {
                    _this.$noty.success('<i class="fas fa-check-circle mr-3"></i>' + _this.$t("tenant_admin_assigned"));
                    _this.loadTenantUsers(); // Reload to update current admin
                })
                .catch(this.onError);
        },
        onError(err) {
        if (err.response != null && err.response.data != null) {
            this.$noty.error('<i class="fas fa-minus-circle mr-3"></i>' + this.$t(err.response.data.message));
        } else {
            console.error(err);
        }
        },
        hasRight(right) {
            if(this.editTenant.createmode == true) return false;
            if (this.editTenant.tenant.rights.includes(right)) return true;
            if (this.editTenant.tenant.role != null) {
            return this.editTenant.tenant.role.rights.includes(right);
            }
            return false;
        },
        getJsonOptions() {
        const options = {
            headers: {
            "Content-Type": "application/json",
            },
        };
        return options;
        },
    },

}
</script>

<style scoped>
.v-text-field--outlined >>> .v-label--active {
  transform: translateY(-14px) scale(0.75) !important;
}

.v-autocomplete.v-text-field--outlined >>> .v-label--active,
.v-select.v-text-field--outlined >>> .v-label--active {
  transform: translateY(-14px) scale(0.75) !important;
}

.v-text-field--outlined.v-text-field--dense >>> .v-label {
  top: 14px;
}

.v-text-field--outlined.v-text-field--dense >>> .v-label--active {
  transform: translateY(-14px) scale(0.75) !important;
}
</style>
